'use client';

import type { UseFormReturn } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import {
  buildAcceptAttribute,
  MediaUploader,
  type MediaFile,
  type MediaPickerLibraryItem,
  type UploadResult,
} from '@kit/uploads';

import type { ApplicationFieldConfig } from '../../types/audition.types';
import {
  buildRequiredRule,
  toPersistedMediaFile,
} from './dynamic-form-renderer-shared';
import { FileLibraryPicker } from './file-library-picker';

type Form = UseFormReturn<Record<string, unknown>>;

export function FileField({
  field,
  form,
  requiredMessage,
  uploadFile,
  deleteFile,
  loadLibrary,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
  uploadFile?: (formData: FormData) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  deleteFile?: (params: { mediaId: string }) => Promise<UploadResult>; // i18n-ignore TS generic type, not UI prose
  /** Adds a "From my library" picker under the uploader. Omit to hide it. */
  loadLibrary?: () => Promise<MediaPickerLibraryItem[]>;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => {
        const multiple = field.allowMultiple === true;
        const stored = formField.value as
          | MediaFile
          | MediaFile[]
          | null
          | undefined;
        // Normalize the stored value to an array for MediaUploader.
        const value: MediaFile[] = Array.isArray(stored)
          ? stored
          : stored && typeof stored === 'object' && 'id' in stored
            ? [stored]
            : [];
        const allowedTypes = field.validation?.fileTypes;
        // The dancer-media server gate relaxes the MIME rule for the `document`
        // bucket ONLY (media-mime-rules.ts). So opt this field into the
        // extension fallback only when every type it allows is PDF — otherwise
        // an unlabelled .jpg on a headshot field would pass here and then be
        // refused by the server, which is the mismatch the prop exists to stop.
        const allowExtensionFallback =
          !!allowedTypes?.length &&
          allowedTypes.every((type) => type === 'application/pdf');

        if (uploadFile) {
          return (
            <FormItem>
              <FormLabel>
                {field.label}
                {field.validation?.required && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </FormLabel>
              <FormControl>
                {multiple ? (
                  <MediaUploader
                    mode="multiple"
                    maxFiles={field.maxFiles}
                    maxFileSize={field.validation?.maxFileSize}
                    allowedTypes={allowedTypes}
                    allowExtensionFallback={allowExtensionFallback}
                    value={value}
                    uploadAction={uploadFile}
                    deleteAction={deleteFile}
                    onUploadComplete={(file) =>
                      formField.onChange([...value, toPersistedMediaFile(file)])
                    }
                    onDelete={(fileId) =>
                      formField.onChange(value.filter((f) => f.id !== fileId))
                    }
                  />
                ) : (
                  <MediaUploader
                    mode="single"
                    maxFileSize={field.validation?.maxFileSize}
                    allowedTypes={allowedTypes}
                    allowExtensionFallback={allowExtensionFallback}
                    value={value}
                    uploadAction={uploadFile}
                    deleteAction={deleteFile}
                    onUploadComplete={(file) =>
                      formField.onChange(toPersistedMediaFile(file))
                    }
                    onDelete={() => formField.onChange(null)}
                  />
                )}
              </FormControl>
              {loadLibrary && (
                <FileLibraryPicker
                  value={value}
                  multiple={multiple}
                  allowedTypes={allowedTypes}
                  maxFiles={field.maxFiles}
                  loadLibrary={loadLibrary}
                  uploadFile={uploadFile}
                  onChange={(next) =>
                    formField.onChange(
                      Array.isArray(next)
                        ? next.map(toPersistedMediaFile)
                        : next && toPersistedMediaFile(next),
                    )
                  }
                />
              )}
              {field.description && (
                <FormDescription>{field.description}</FormDescription>
              )}
              <FormMessage />
            </FormItem>
          );
        }

        return (
          <FormItem>
            <FormLabel>
              {field.label}
              {field.validation?.required && (
                <span className="text-destructive ml-1">*</span>
              )}
            </FormLabel>
            <FormControl>
              <Input
                type="file"
                accept={buildAcceptAttribute(allowedTypes)}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  formField.onChange(file || null);
                }}
              />
            </FormControl>
            {field.description && (
              <FormDescription>{field.description}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
